import { useState } from 'react'
import { useNavigate, useLocation, useOutletContext } from 'react-router'
import clsx from 'clsx'
import DOMPurify from 'dompurify'
import {
  FaUser,
  FaRegComment,
  FaRegHeart,
  FaHeart,
  FaTrashAlt,
} from 'react-icons/fa'
import { LuDot } from 'react-icons/lu'

import { useAppDispatch } from '../../hooks/redux'
import { CurrentUser } from '../../services/schema/User'
import {
  useGetPostByIdQuery,
  useUpdatePostLikesMutation,
  useDeletePostMutation,
} from '../../services/apiSlice'
import { setPrevLocation } from '../../services/appSlice'
import { stringShortener } from '../../services/helpers'
import SingleVideo from './SingleVideo'
import Filler from '../utils/Filler'

interface Props {
  id: string
  username: string
  prev: string
  canBeDeleted: boolean
}

const PostCard: React.FC<Props> = ({ id, username, prev, canBeDeleted }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const user = useOutletContext<CurrentUser>()
  const [updatePostLikes] = useUpdatePostLikesMutation()
  const [deletePost] = useDeletePostMutation()
  const [isLiked, setIsLiked] = useState(user.likedPosts?.includes(id))
  const [debouncer, setDebouncer] = useState<
    ReturnType<typeof setTimeout> | undefined
  >(undefined)

  const { data, isSuccess } = useGetPostByIdQuery(
    { postId: id, username: username },
    { skip: !id },
  )

  const post = isSuccess && data && data

  if (post) {
    const slicedPathname = location.pathname.slice(1)
    const sanitized =
      post.postText !== null
        ? DOMPurify.sanitize(post.postText as string)
        : null
    const media = post.postMedia ? post.postMedia.split(',') : null
    const mediaTypes = post.mediaTypes ? post.mediaTypes.split(',') : null

    const usernameShort = stringShortener(post.username, 11)
    const displayNameShort = stringShortener(post.displayName, 11)

    const profilePic =
      post.profilePicture !== null ? post.profilePicture : '/user_sample.jpg'

    const toggleLike = () => {
      const newState = !isLiked

      setIsLiked(newState)

      if (debouncer) {
        clearTimeout(debouncer)
        setDebouncer(undefined)
        return
      }

      const timeout = setTimeout(() => {
        // Place backend API request here...
        updatePostLikes({
          postId: post.postId,
          username: post.username,
          type: `${newState ? 'add' : 'remove'}`,
        })

        setDebouncer(undefined)
      }, 1000)

      setDebouncer(timeout)
    }

    let mediaDisplay

    if (media && mediaTypes) {
      mediaDisplay = media.map((m, idx) => {
        if (mediaTypes[idx] === 'video') {
          if (mediaTypes.length > 1) {
            return (
              <div
                key={m}
                className="relative flex h-full w-full grow flex-col items-center justify-center rounded-[30px] object-contain pr-[5px] pb-[5px]"
              >
                <video className="h-full w-full rounded-[30px]" controls>
                  <source src={m} />
                </video>
              </div>
            )
          } else {
            return (
              <div
                key={m}
                className="relative flex h-full w-full grow flex-col items-center justify-center rounded-[30px] object-contain pr-[5px] pb-[5px]"
              >
                <SingleVideo src={m} />
              </div>
            )
          }
        }

        if (mediaTypes[idx] === 'image') {
          const inline = {
            backgroundImage: 'url(' + m + ')',
          }
          return (
            <div
              key={m}
              className="relative flex h-full w-full min-w-0 grow flex-col items-center justify-center rounded-[30px] object-contain pr-[5px] pb-[5px]"
            >
              <div
                style={inline}
                className="absolute inset-x-0 inset-y-0 h-full w-full min-w-0 rounded-[30px] bg-cover bg-center bg-no-repeat"
              />
              {/* I patterned this layout from twitter/x itself and noticed 2 separate elements displaying the src... why do we need a separate div though if img does the job? maybe for the alt? or for screen readers / aria? */}
              <img
                className="absolute inset-x-0 inset-y-0 h-full w-full min-w-0 rounded-[30px] opacity-0"
                src={m}
                alt={m}
              />
            </div>
          )
        }
      })
    }

    const main = (
      <div className="flex h-auto w-full flex-col">
        <div className="flex w-full justify-between">
          <div className="flex items-center">
            <button
              type="button"
              data-typeid="post-profile-btn"
              onClick={() => {
                if (slicedPathname === post.username) {
                  return
                }
                dispatch(
                  setPrevLocation({
                    loc: prev,
                    scroll: window.scrollY.toString(),
                    url: location.pathname,
                  }),
                )
                navigate(`/${post.username}`, { replace: true })
              }}
              className="flex cursor-pointer items-center"
            >
              <div className="hover:underline hover:underline-offset-2">
                <p>{displayNameShort}</p>
              </div>
              <span className="text-blue pl-[5px]">
                <FaUser />
              </span>
            </button>
            <button
              type="button"
              data-typeid="post-profile-btn"
              onClick={() => {
                if (slicedPathname === post.username) {
                  return
                }
                dispatch(
                  setPrevLocation({
                    loc: prev,
                    scroll: window.scrollY.toString(),
                    url: location.pathname,
                  }),
                )
                navigate(`/${post.username}`, { replace: true })
              }}
              className="text-sub cursor-pointer pl-[5px]"
            >
              <div>
                <p>@{usernameShort}</p>
              </div>
            </button>
            <div className="text-sub flex items-center pl-[2px]">
              <LuDot />
            </div>
            <div className="text-sub pl-[2px]">
              <p>
                {post.timeline.withinTheYear
                  ? post.timeline.short
                  : post.timeline.long}
              </p>
            </div>
          </div>
          {canBeDeleted && (
            <div>
              <button
                data-typeid="delete-post-btn"
                type="button"
                className="hover-main text-red cursor-pointer rounded-full p-[8px] text-base disabled:cursor-not-allowed disabled:opacity-50"
                // disabled={true}
                onClick={() => {
                  if (confirm('Are you sure you want to delete this post?')) {
                    deletePost({
                      postId: id,
                      username: username,
                    })
                  }
                }}
              >
                <FaTrashAlt />
              </button>
            </div>
          )}
        </div>
        <div
          className="w-full pb-[10px]"
          dangerouslySetInnerHTML={{ __html: sanitized as string }}
        />
        <div
          className={clsx(
            'relative flex w-full',
            media?.length === 1 && 'h-[450px] lg:h-[500px]',
            media?.length === 2 && 'h-[300px]',
          )}
        >
          {mediaDisplay ? mediaDisplay : <Filler />}
        </div>
        <div className="text-sub flex h-auto w-full justify-evenly pt-[5px] pb-[8px]">
          <div className="flex h-auto w-auto grow items-center">
            <button
              data-typeid="post-reply-btn"
              type="button"
              className="hover-blue relative flex h-[30px] w-auto cursor-pointer items-center"
            >
              <div className="flex h-[30px] w-[30px] items-center justify-center rounded-full">
                <FaRegComment />
              </div>
              <p>{post.replyCount}</p>
            </button>
          </div>
          <div className="flex h-auto w-auto grow items-center">
            <button
              data-typeid="post-like-btn"
              type="button"
              className="hover-red relative flex h-[30px] w-auto cursor-pointer items-center"
              onClick={() => {
                toggleLike()
              }}
            >
              <div className="flex h-[30px] w-[30px] items-center justify-center rounded-full">
                {isLiked ? <FaHeart className="text-red" /> : <FaRegHeart />}
              </div>
              <p>{post.likeCount}</p>
            </button>
          </div>
          <div className="relative flex h-[30px] w-auto grow items-center"></div>
        </div>
      </div>
    )

    return (
      <section
        className="border-theme hover-sub flex h-auto w-full cursor-pointer self-stretch border-r border-b border-l pr-[25px] pl-[15px]"
        onClick={(e) => {
          const el = e.target as HTMLElement
          if (el.tagName === 'VIDEO') {
            return
          }
          if (
            el.closest('button')?.dataset.typeid === 'post-profile-btn' ||
            el.closest('button')?.dataset.typeid === 'post-reply-btn' ||
            el.closest('button')?.dataset.typeid === 'post-like-btn' ||
            el.closest('button')?.dataset.typeid === 'delete-post-btn' ||
            el.closest('div')?.dataset.typeid === 'profile-picture'
          ) {
            return
          }
          dispatch(
            setPrevLocation({
              loc: prev,
              scroll: window.scrollY.toString(),
              url: location.pathname,
            }),
          )
          navigate(`/${post.username}/${post.postId}`, { replace: true })
        }}
      >
        <article className="h-auto w-full">
          <div className="flex h-auto pt-[10px]">
            <div className="mr-[8px] h-auto w-[50px] shrink-0 select-none">
              <div
                data-typeid="profile-picture"
                onClick={() => {
                  navigate(`/${post.username}`, { replace: true })
                }}
                className="flex h-[40px] w-[40px] cursor-pointer items-center justify-center rounded-full bg-cover bg-center"
                style={{
                  backgroundImage: `url(${profilePic})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
            </div>
            {main}
          </div>
        </article>
      </section>
    )
  }
}

export default PostCard
